import {
  ExpenseTypes,
  type ExpenseTypeEnum,
} from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'

export type TaxType = '%' | '$'

interface ComputeExpenseLineItemTotalsParams {
  expenseType: ExpenseTypeEnum
  subtotal: number
  taxAmount: number
  taxType: TaxType
  tips: number
  mileageRate: number
  distance: number
  hourlyRate: number
  hoursOfWork: number
}

interface ComputeExpenseLineItemTotalsResult {
  computedSubtotal: number
  computedTaxAmount: number
  computedTotal: number
}

interface ComputeSubtotalFromTotalParams {
  total: number
  /** raw input value — percentage number when taxType is '%', dollar amount when '$' */
  taxAmount: number
  taxType: TaxType
  expenseType: ExpenseTypeEnum
  /** Ignored for non-MEALS expense types; `executeReverse` only subtracts tips when expenseType is MEALS. */
  tips: number
}

interface ComputeSubtotalFromTotalResult {
  computedSubtotal: number
}

// Nudge in cents-space so the epsilon survives at currency magnitudes
// (`Number.EPSILON` alone is only meaningful near magnitude 1, so e.g.
// round(10.005) would otherwise collapse to 10 instead of 10.01). Rounding
// is done in absolute space with the sign reapplied so halves round
// symmetrically away from zero for negatives — Math.round's default
// half-to-+∞ behaviour would otherwise give e.g. -1.015 → -1.01 while
// +1.015 → +1.02.
const roundToCents = (value: number): number => {
  if (value === 0) return 0
  const sign = value < 0 ? -1 : 1
  return (sign * Math.round(Math.abs(value) * 100 + Number.EPSILON * 100)) / 100
}

const computeSubtotal = (
  values: ComputeExpenseLineItemTotalsParams
): number => {
  switch (values.expenseType) {
    case ExpenseTypes.MILEAGE:
      return roundToCents(values.mileageRate * values.distance)
    case ExpenseTypes.LABOUR:
      return roundToCents(values.hourlyRate * values.hoursOfWork)
    default:
      return values.subtotal
  }
}

const computeTaxAmount = (
  subtotal: number,
  taxAmount: number,
  taxType: TaxType
): number => {
  if (taxType === '%') {
    return roundToCents(subtotal * (taxAmount / 100))
  }
  return taxAmount
}

const computeTotal = (
  subtotal: number,
  taxAmount: number,
  tips: number,
  expenseType: ExpenseTypeEnum
): number => {
  const base = subtotal + taxAmount
  if (expenseType === ExpenseTypes.MEALS) {
    return roundToCents(base + tips)
  }
  return roundToCents(base)
}

export class ComputeExpenseLineItemTotalsUseCase {
  execute = (
    values: ComputeExpenseLineItemTotalsParams
  ): ComputeExpenseLineItemTotalsResult => {
    const computedSubtotal = computeSubtotal(values)
    const computedTaxAmount = computeTaxAmount(
      computedSubtotal,
      values.taxAmount,
      values.taxType
    )
    const computedTotal = computeTotal(
      computedSubtotal,
      computedTaxAmount,
      values.tips,
      values.expenseType
    )

    return {
      computedSubtotal,
      computedTaxAmount,
      computedTotal,
    }
  }

  executeReverse = (
    values: ComputeSubtotalFromTotalParams
  ): ComputeSubtotalFromTotalResult => {
    const effectiveTips =
      values.expenseType === ExpenseTypes.MEALS ? values.tips : 0

    let computedSubtotal: number

    if (values.taxType === '%') {
      const denominator = 1 + values.taxAmount / 100
      if (denominator <= 0) {
        computedSubtotal = 0
      } else {
        computedSubtotal = roundToCents(
          (values.total - effectiveTips) / denominator
        )
      }
    } else {
      computedSubtotal = roundToCents(
        values.total - values.taxAmount - effectiveTips
      )
    }

    return { computedSubtotal }
  }
}
